/**
 * Client Portal - Invoice List
 *
 * Shows client's invoices with status and amounts.
 * Read-only view - no payment functionality.
 *
 * @see Story 4.4: Invoice Viewing in Client Portal
 */

import { Button } from "@designs-by-maryam/ui/components/button";
import { Skeleton } from "@designs-by-maryam/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, FileText, Receipt } from "lucide-react";
import { requireClient } from "@/functions/require-role";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/portal/invoices/")({
	component: InvoiceList,
	beforeLoad: async () => {
		await requireClient();
	},
});

type InvoiceStatus = "draft" | "pending" | "paid" | "overdue" | "cancelled";

const STATUS_CONFIG: Record<
	InvoiceStatus,
	{ label: string; color: string; bgColor: string }
> = {
	draft: {
		label: "Draft",
		color: "text-gray-700 dark:text-gray-300",
		bgColor: "bg-gray-100 dark:bg-gray-800",
	},
	pending: {
		label: "Pending",
		color: "text-yellow-700 dark:text-yellow-300",
		bgColor: "bg-yellow-100 dark:bg-yellow-900/50",
	},
	paid: {
		label: "Paid",
		color: "text-green-700 dark:text-green-300",
		bgColor: "bg-green-100 dark:bg-green-900/50",
	},
	overdue: {
		label: "Overdue",
		color: "text-red-700 dark:text-red-300",
		bgColor: "bg-red-100 dark:bg-red-900/50",
	},
	cancelled: {
		label: "Cancelled",
		color: "text-gray-500 dark:text-gray-400",
		bgColor: "bg-gray-100 dark:bg-gray-800",
	},
};

function InvoiceList() {
	const { data: invoices, isLoading } = useQuery(
		orpc.portalGetMyInvoices.queryOptions(),
	);

	// Calculate summary stats
	const totalPending =
		invoices
			?.filter((inv) => inv.status === "pending" || inv.status === "overdue")
			.reduce((sum, inv) => sum + inv.totalBhd, 0) || 0;

	const totalPaid =
		invoices
			?.filter((inv) => inv.status === "paid")
			.reduce((sum, inv) => sum + inv.totalBhd, 0) || 0;

	return (
		<div className="container mx-auto px-4 py-8 md:px-6">
			{/* Back Button */}
			<Link
				className="mb-6 inline-flex items-center text-muted-foreground hover:text-foreground"
				to="/portal"
			>
				<ArrowLeft className="mr-2 h-4 w-4" />
				Back to Dashboard
			</Link>

			{/* Header */}
			<div className="mb-8">
				<h1 className="font-bold text-2xl md:text-3xl">Invoices</h1>
				<p className="mt-1 text-muted-foreground">
					View your invoices and payment history
				</p>
			</div>

			{/* Summary Cards */}
			<div className="mb-8 grid gap-4 md:grid-cols-3">
				<div className="rounded-lg border bg-card p-4">
					<div className="flex items-center gap-3">
						<div className="rounded-full bg-yellow-100 p-2 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300">
							<Receipt className="h-5 w-5" />
						</div>
						<div>
							<p className="text-muted-foreground text-sm">Amount Due</p>
							{isLoading ? (
								<Skeleton className="mt-1 h-6 w-24" />
							) : (
								<p className="font-bold text-xl">
									BHD {(totalPending / 1000).toFixed(3)}
								</p>
							)}
						</div>
					</div>
				</div>
				<div className="rounded-lg border bg-card p-4">
					<div className="flex items-center gap-3">
						<div className="rounded-full bg-green-100 p-2 text-green-700 dark:bg-green-900/50 dark:text-green-300">
							<Receipt className="h-5 w-5" />
						</div>
						<div>
							<p className="text-muted-foreground text-sm">Total Paid</p>
							{isLoading ? (
								<Skeleton className="mt-1 h-6 w-24" />
							) : (
								<p className="font-bold text-xl">
									BHD {(totalPaid / 1000).toFixed(3)}
								</p>
							)}
						</div>
					</div>
				</div>
				<div className="rounded-lg border bg-card p-4">
					<div className="flex items-center gap-3">
						<div className="rounded-full bg-primary/10 p-2 text-primary dark:bg-primary/20 dark:text-primary">
							<FileText className="h-5 w-5" />
						</div>
						<div>
							<p className="text-muted-foreground text-sm">Total Invoices</p>
							{isLoading ? (
								<Skeleton className="mt-1 h-6 w-12" />
							) : (
								<p className="font-bold text-xl">{invoices?.length || 0}</p>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Invoice Table */}
			<InvoicesTable invoices={invoices} isLoading={isLoading} />
		</div>
	);
}

type InvoiceData = {
	id: string;
	invoiceNumber: string;
	createdAt: string;
	totalFormatted: string;
	dueDate: string | null;
	status: string;
};

type InvoicesTableProps = {
	isLoading: boolean;
	invoices: InvoiceData[] | undefined;
};

function InvoicesTable({ isLoading, invoices }: InvoicesTableProps) {
	if (isLoading) {
		return (
			<div className="space-y-4">
				{[1, 2, 3].map((n) => (
					<Skeleton className="h-16 w-full rounded-lg" key={`skeleton-${n}`} />
				))}
			</div>
		);
	}

	if (invoices && invoices.length > 0) {
		return (
			<div className="overflow-x-auto rounded-lg border">
				<table className="w-full">
					<thead className="border-b bg-muted/50">
						<tr>
							<th className="px-4 py-3 text-left font-medium text-muted-foreground text-sm">
								Invoice
							</th>
							<th className="px-4 py-3 text-left font-medium text-muted-foreground text-sm">
								Date
							</th>
							<th className="px-4 py-3 text-left font-medium text-muted-foreground text-sm">
								Amount
							</th>
							<th className="px-4 py-3 text-left font-medium text-muted-foreground text-sm">
								Due Date
							</th>
							<th className="px-4 py-3 text-left font-medium text-muted-foreground text-sm">
								Status
							</th>
							<th className="px-4 py-3 text-right font-medium text-muted-foreground text-sm">
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{invoices.map((invoice) => {
							const status =
								STATUS_CONFIG[invoice.status as InvoiceStatus] ||
								STATUS_CONFIG.pending;
							return (
								<tr
									className="border-b last:border-0 hover:bg-muted/50"
									key={invoice.id}
								>
									<td className="px-4 py-3">
										<Link
											className="font-medium text-primary hover:underline"
											params={{ id: invoice.id }}
											to="/portal/invoices/$id"
										>
											{invoice.invoiceNumber}
										</Link>
									</td>
									<td className="px-4 py-3 text-muted-foreground text-sm">
										{new Date(invoice.createdAt).toLocaleDateString()}
									</td>
									<td className="px-4 py-3 font-medium">
										{invoice.totalFormatted}
									</td>
									<td className="px-4 py-3 text-muted-foreground text-sm">
										{invoice.dueDate
											? new Date(invoice.dueDate).toLocaleDateString()
											: "-"}
									</td>
									<td className="px-4 py-3">
										<span
											className={`inline-flex rounded-full px-2 py-1 font-medium text-xs ${status.bgColor} ${status.color}`}
										>
											{status.label}
										</span>
									</td>
									<td className="px-4 py-3 text-right">
										<Link params={{ id: invoice.id }} to="/portal/invoices/$id">
											<Button size="sm" variant="ghost">
												View
												<ArrowRight className="ml-1 h-4 w-4" />
											</Button>
										</Link>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}

	return (
		<div className="rounded-lg border bg-card p-12 text-center">
			<Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
			<h3 className="mt-4 font-semibold text-lg">No invoices yet</h3>
			<p className="mt-2 text-muted-foreground">
				Invoices will appear here once they are created for your projects
			</p>
		</div>
	);
}
